import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '@core/authStore';

const SIGNUPS_DISABLED_PATTERN = /signups?\s+not\s+allowed|signup_disabled/i;

/**
 * Atlas client login. Email/password + Google + LinkedIn via Supabase.
 * Signups are disabled in prod, so register / signup-disabled OAuth land on
 * a simple invite-only notice (the richer early-access waitlist lives on the
 * marketing frontend).
 */
export function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, loginWithGoogle, loginWithLinkedIn, loading } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [signupBlocked, setSignupBlocked] = useState(
    Boolean((location.state as { signupBlocked?: boolean } | null)?.signupBlocked),
  );

  useEffect(() => {
    if (isAuthenticated) navigate('/', { replace: true });
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const result = await login(email, password);
    if (result.error) {
      if (SIGNUPS_DISABLED_PATTERN.test(result.error)) {
        setSignupBlocked(true);
        return;
      }
      setError(result.error);
    }
  };

  const handleGoogle = async () => {
    setError(null);
    const result = await loginWithGoogle();
    if (result.error) setError(result.error);
  };

  const handleLinkedIn = async () => {
    setError(null);
    const result = await loginWithLinkedIn();
    if (result.error) setError(result.error);
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#1C1B19' }}>
      <div className="w-full max-w-sm">
        <div className="text-center mb-10">
          <h1 className="text-2xl tracking-[-0.05em] font-extralight" style={{ color: '#E8E3D9' }}>
            earthbenders
          </h1>
          <div className="mt-2 h-px w-12 mx-auto" style={{ background: '#C4654A' }} />
          <p className="mt-3 text-xs" style={{ color: '#9C9585' }}>
            análise territorial
          </p>
        </div>

        {signupBlocked ? (
          <div
            className="rounded-lg p-5 text-sm"
            style={{ background: '#2A2825', color: '#E8E3D9', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <p className="font-medium mb-2" style={{ color: '#C4654A' }}>Acesso por convite</p>
            <p style={{ color: '#9C9585' }}>
              O registo está fechado. Se já tem conta, inicie sessão. Para pedir acesso,
              contacte a equipa.
            </p>
            <button
              type="button"
              onClick={() => { setSignupBlocked(false); setError(null); }}
              className="mt-4 text-xs underline"
              style={{ color: '#C4654A' }}
            >
              Voltar ao início de sessão
            </button>
          </div>
        ) : (
          <>
            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="email"
                className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                style={{ background: '#2A2825', color: '#E8E3D9', border: '1px solid rgba(255,255,255,0.1)' }}
              />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="palavra-passe"
                className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                style={{ background: '#2A2825', color: '#E8E3D9', border: '1px solid rgba(255,255,255,0.1)' }}
              />
              {error && <p className="text-xs" style={{ color: '#D7191C' }}>{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-lg px-3 py-2 text-sm font-medium disabled:opacity-60"
                style={{ background: '#C4654A', color: '#E8E3D9' }}
              >
                {loading ? 'A entrar...' : 'Entrar'}
              </button>
            </form>

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
              <span className="text-xs" style={{ color: '#9C9585' }}>ou</span>
              <div className="flex-1 h-px" style={{ background: 'rgba(255,255,255,0.1)' }} />
            </div>

            <div className="space-y-2">
              <button
                type="button"
                onClick={handleGoogle}
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{ background: '#2A2825', color: '#E8E3D9', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Entrar com Google
              </button>
              <button
                type="button"
                onClick={handleLinkedIn}
                className="w-full rounded-lg px-3 py-2 text-sm"
                style={{ background: '#2A2825', color: '#E8E3D9', border: '1px solid rgba(255,255,255,0.1)' }}
              >
                Entrar com LinkedIn
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
